/**
 * Лента: строка на вилку, раскрытие в карточку, пауза под курсором.
 *
 * Пауза — не удобство, а защита от потери денег: пользователь копирует сумму со
 * строки, лента в этот момент переставляется, и он копирует сумму другой вилки.
 * Поэтому пока курсор внутри ленты, порядок и состав видимых строк заморожены, а
 * счётчик показывает, сколько изменений ждёт снаружи.
 *
 * Возраст тикает локально раз в секунду: сервер не гоняет дельту ради счётчика.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { FRESH_MS, beep, newcomers, worthSound } from '../lib/alert';
import { oddsText } from '../lib/calc';
import {
  type Fork,
  type Middle,
  ageOf,
  entryHint,
  entryUrl,
  marketLabel,
  roiValue,
  sportLabel,
} from '../lib/protocol';
import { badgeText, rulesHint } from '../lib/rules';
import { bankVerdict, volumeHint, volumeText, volumeTone } from '../lib/volume';
import {
  type EventGroup,
  groupByEvent,
  health,
  pendingChanges,
  renderedForks,
  useFeed,
} from '../store/feed';
import { ForkCard } from './ForkCard';

/** Раз в секунду перерисовать возраст. Ленту это не переставляет. */
function useSecond(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/**
 * Отметить приехавшие вилки и, если просили, дать знать звуком.
 *
 * Первый снапшот не считается приходом новых вилок: они существовали до того,
 * как открыли вкладку. Иначе каждое переподключение — а оно бывает при любой
 * пересборке сети — превращалось бы в очередь сигналов ни о чём.
 *
 * Возвращает карту «id → до какого момента подсвечивать». Подсветка не зависит
 * от паузы: пауза защищает руку, копирующую сумму, а не глаз.
 */
function useArrivals(forks: Map<string, Fork>, hidden: string[]): Record<string, number> {
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<Record<string, number>>({});
  const sound = useFeed((s) => s.sound);
  const soundRoi = useFeed((s) => s.soundRoi);
  // Ревизия, а не факт первого вызова: страница монтируется с пустым стором, и
  // снапшот приезжает уже после. Без этой проверки открытие вкладки означало бы
  // очередь сигналов обо всех вилках рынка сразу.
  const revision = useFeed((s) => s.revision);

  useEffect(() => {
    const known = seen.current;
    seen.current = new Set(forks.keys());
    if (known === null || revision <= 1) return;

    const arrived = newcomers(forks.values(), known);
    if (arrived.length === 0) return;

    const until = Date.now() + FRESH_MS;
    setFresh((prev) => {
      const now = Date.now();
      const next: Record<string, number> = {};
      for (const [id, expiry] of Object.entries(prev)) if (expiry > now) next[id] = expiry;
      for (const fork of arrived) next[fork.id] = until;
      return next;
    });
    // Скрытую вручную вилку человек уже видел и отказался от неё — звонить о
    // её возвращении значит учить выключать звук.
    if (sound && worthSound(arrived.filter((fork) => !hidden.includes(fork.id)), soundRoi)) {
      beep();
    }
  }, [forks, revision, hidden, sound, soundRoi]);

  return fresh;
}

const roiClass = (value: number): string => {
  if (value >= 5) return 'roi hot';
  if (value < 1) return 'roi thin';
  return 'roi';
};

export function Feed() {
  const now = useSecond();
  // Из стора берутся только ссылки, которые в нём лежат: селектор, собирающий новый
  // массив, уводит React в бесконечный цикл рендера (см. комментарий в store/feed.ts).
  // Видимый список считается здесь.
  const all = useFeed((s) => s.forks);
  const filters = useFeed((s) => s.filters);
  const frozen = useFeed((s) => s.frozen);
  const hidden = useFeed((s) => s.hidden);
  const pending = useFeed(pendingChanges);
  const truncated = useFeed((s) => s.truncated);
  const bank = useFeed((s) => s.bank);
  const expanded = useFeed((s) => s.expanded);
  const setPaused = useFeed((s) => s.setPaused);
  const unhideAll = useFeed((s) => s.unhideAll);

  const fresh = useArrivals(all, hidden);

  const forks = useMemo(
    () => renderedForks({ forks: all, filters, hidden, frozen }, now),
    [all, filters, hidden, frozen, now],
  );
  const groups = useMemo(() => groupByEvent(forks), [forks]);

  return (
    <div
      className="feed"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="head">
        <span>net roi · на ваш банк</span>
        <span>дисциплина · лига</span>
        <span>нога 1</span>
        <span>нога 2</span>
        <span>живучесть · возраст · статус</span>
        <span />
      </div>

      {forks.length === 0 && <Empty />}

      {groups.map((group) => (
        <EventRows
          key={group.key}
          group={group}
          now={now}
          expanded={expanded}
          bank={bank}
          fresh={fresh}
        />
      ))}

      <p className="notice" style={{ marginTop: 10, border: 'none' }}>
        {pending > 0 && <>лента на паузе, изменений в очереди: {pending}. </>}
        {hidden.length > 0 && (
          <>
            скрыто вами: {hidden.length}{' '}
            <button type="button" className="chip" onClick={unhideAll}>
              вернуть
            </button>{' '}
          </>
        )}
        {truncated > 0 && <>не показано из-за потолка кадра: {truncated}</>}
      </p>

      <Middles now={now} />
    </div>
  );
}

/**
 * Полувилки: две ставки, которые могут выиграть ОБЕ.
 *
 * Отдельной секцией под лентой, а не строками вперемешку с вилками, и это не
 * вопрос вёрстки. У вилки прибыль гарантирована, у полувилки — нет: вне окна одна
 * нога проигрывает, и попытка стоит денег. Строка полувилки в общем списке
 * читалась бы как вилка с очень большой отдачей — то есть как обещание гарантии
 * там, где её нет. Пока жанр не назван словами, показывать его нельзя.
 *
 * Секции нет вовсе, когда полувилок нет: пустой заголовок учил бы пролистывать
 * место, где иногда появляется главное.
 */
function Middles({ now }: { now: number }) {
  const rows = useFeed((s) => s.middles);
  if (rows.length === 0) return null;

  return (
    <div className="middles">
      <p className="notice" style={{ textAlign: 'left', borderStyle: 'solid' }}>
        <b style={{ color: 'var(--text)' }}>полувилки: прибыль НЕ гарантирована.</b> обе
        ставки выигрывают, только если итог попадёт в окно между линиями. промах стоит
        денег — сколько именно, написано в первой колонке слева от выплаты.
      </p>

      <div className="head">
        <span>промах · попадание</span>
        <span>дисциплина · лига</span>
        <span>нога 1 · ставка</span>
        <span>нога 2 · ставка</span>
        <span>окно · возраст</span>
        <span />
      </div>

      {rows.map((row) => (
        <MiddleRow key={row.id} row={row} now={now} />
      ))}
    </div>
  );
}

function MiddleRow({ row, now }: { row: Middle; now: number }) {
  const legs = row.legs ?? [];
  const age = ageOf(row, now);
  const cost = Number.parseFloat(row.cost ?? '');
  const outcomes = row.outcomes ?? 0;

  return (
    <div className="row middle">
      {/* Два числа в одной колонке, и промах стоит ПЕРВЫМ. Порядок здесь несёт
          смысл: назвать сначала выплату — значит продать лотерею как арбитраж,
          а цена ошибки тут та же, что у фантомной вилки. */}
      <div className="roi thin" title="слева — сколько теряется при промахе, справа — сколько приносит попадание">
        <span className={Number.isFinite(cost) && cost <= 0 ? 'good' : 'bad'}>
          {Number.isFinite(cost) && cost <= 0 ? 'без потерь' : `−${row.cost ?? '—'}%`}
        </span>
        <small style={{ color: 'var(--good)' }}>+{row.payout ?? '—'}%</small>
      </div>

      <div className="event">
        <b title={`${sportLabel(row.sport)}${row.league ? ` · ${row.league}` : ''}`}>
          {sportLabel(row.sport)}
          {row.league && <em>{row.league}</em>}
        </b>
        <span>{marketLabel(row.market, row.sport)}</span>
      </div>

      {[0, 1].map((index) => {
        const leg = legs[index];
        const stake = row.stakes?.[index];
        return (
          <div className="leg" key={`${row.id}:leg:${index}`}>
            <span className="book">
              {entryUrl(leg) ? (
                <a
                  href={entryUrl(leg) as string}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={entryHint(leg)}
                >
                  {leg?.s}
                </a>
              ) : (
                (leg?.s ?? '—')
              )}
            </span>
            {/* Раскладка показана, а не оставлена на счёт в уме: она уравнивает
                оба промаха, и посчитанная человеком «на глаз» превращает
                известный риск в неизвестный. */}
            <span className="num" style={{ color: 'var(--dimmer)', fontSize: 12 }}>
              {stake ? `${stake}% банка` : ''}
            </span>
            <span className="odds">{row.o?.[index] ? oddsText(row.o[index]) : '—'}</span>
          </div>
        );
      })}

      <div className="foot">
        {/* Окно считается в целых исходах, а не в очках: попадание — это когда
            итог оказался одним из них, и длина отрезка тут ничего не говорит. */}
        <span className="vol" title="сколько целых исходов выигрывают обе ставки сразу">
          окно: {outcomes === 1 ? '1 исход' : `${outcomes} исхода`}
        </span>
        <span className={`age${age > 120 ? ' age old' : ''}`}>{age}с</span>
      </div>

      <div className="actions" />
    </div>
  );
}

/**
 * Пустая лента с названной причиной.
 *
 * «Вилок нет» — правда только когда движок сканирует и источники отвечают. В
 * остальных случаях это молчание вместо аварии, а тишина хуже падения: человек видит
 * спокойный рынок там, где у нас не работает сканер.
 */
function Empty() {
  const sources = useFeed((s) => s.sources);
  const state = health({ sources });
  if (state.kind === 'engine_off') {
    return (
      <p className="notice alarm">
        движок не сканирует: ни один источник не подключён. это не пустой рынок — это
        выключенный сканер
      </p>
    );
  }
  if (state.kind === 'silent') {
    return (
      <p className="notice alarm">
        источники молчат: {state.sources.join(', ')}. ставить по этой ленте нельзя, пока
        связь не восстановится
      </p>
    );
  }
  return (
    <p className="notice">
      вилок нет. это нормальное состояние ленты: настоящих вилок в киберспорте единицы в
      час, а не десятки в минуту
    </p>
  );
}

/**
 * Один матч: лучшая вилка строкой, остальные — под раскрытием.
 *
 * Свёрнутые строки не спрятаны, а свёрнуты: число рядом называет, сколько их, и
 * одно нажатие показывает все. Разница существенная — спрятать значило бы решить
 * за человека, что другие рынки этого матча ему не нужны, а на карточном рынке
 * это как раз то, ради чего он и пришёл.
 */
function EventRows({
  group,
  now,
  expanded,
  bank,
  fresh,
}: {
  group: EventGroup;
  now: number;
  expanded: string | null;
  bank: number;
  fresh: Record<string, number>;
}) {
  const [open, setOpen] = useState(false);
  const rows = open ? [group.head, ...group.rest] : [group.head];

  return (
    <>
      {rows.map((fork) => (
        <Row
          key={fork.id}
          fork={fork}
          now={now}
          open={expanded === fork.id}
          bank={bank}
          fresh={(fresh[fork.id] ?? 0) > now}
        />
      ))}
      {group.rest.length > 0 && (
        <button type="button" className="more" onClick={() => setOpen(!open)}>
          {open
            ? 'свернуть'
            : `ещё ${group.rest.length} на этом матче: другие рынки и карты`}
        </button>
      )}
    </>
  );
}

function Row({
  fork,
  now,
  open,
  bank,
  fresh,
}: {
  fork: Fork;
  now: number;
  open: boolean;
  bank: number;
  fresh: boolean;
}) {
  const toggleExpanded = useFeed((s) => s.toggleExpanded);
  const hide = useFeed((s) => s.hide);
  const age = ageOf(fork, now);
  const legs = fork.legs ?? [];
  const rules = fork.rl ?? [];
  const flagged = fork.st === 'flagged';
  // Приговор для банка считается здесь, а не в сторе: он зависит от поля ввода,
  // которое человек крутит, и пересчитывать из-за него весь список незачем.
  const verdict = bankVerdict(fork, bank);

  return (
    <>
      {/* data-fork-row — договор с гейтом выката, а не разметка. `render-check.mjs`
          считает по нему строки ленты и роняет выкат, если при живом снапшоте их
          ноль. Имена классов принадлежат оформлению и меняются свободно; этот
          атрибут обязан пережить любую перекройку. */}
      <div
        data-fork-row=""
        className={`row${flagged ? ' flagged' : ''}${open ? ' open' : ''}${fresh ? ' fresh' : ''}`}
      >
        <button
          type="button"
          className={roiClass(roiValue(fork))}
          onClick={() => toggleExpanded(fork.id)}
          title="раскрыть раскладку"
        >
          {fork.roi ?? '—'}%
          {/* Под главным числом — то, что получится на ЕГО банк. Заголовочный ROI
              честен для вершины стакана, а ставят не вершину: вторая нога идёт
              глубже, и на 500 долларов процент уже другой. Конкурент показывает
              одно число и молчит про глубину — это и есть способ потерять деньги
              на «красивой» вилке. */}
          <small className={`atbank ${volumeTone(verdict)}`} title={volumeHint(fork, verdict)}>
            {volumeText(verdict, bank)}
          </small>
        </button>

        <div className="event">
          <b title={`${sportLabel(fork.sport)}${fork.league ? ` · ${fork.league}` : ''}`}>
            {sportLabel(fork.sport)}
            {/* Лига заполнена там, где её отдаёт источник. У конкурента это поле
                пустое во всех сигналах — он не смапил дерево турниров вовсе, а
                уровень турнира решает: на T3-квалификации лимиты копеечные. */}
            {fork.league && <em>{fork.league}</em>}
          </b>
          <span>{marketLabel(fork.market, fork.sport)}</span>
        </div>

        {[0, 1].map((index) => {
          const leg = legs[index];
          return (
            <div className="leg" key={`${fork.id}:leg:${index}`}>
              <span className="book">
                {entryUrl(leg) ? (
                  <a
                    href={entryUrl(leg) as string}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={entryHint(leg)}
                    className={leg?.direct === false ? 'indirect' : undefined}
                  >
                    {leg?.s}
                    {leg?.direct === false && '↗'}
                  </a>
                ) : (
                  (leg?.s ?? '—')
                )}
              </span>
              <span className="num" style={{ color: 'var(--dimmer)', fontSize: 12 }}>
                {leg ? (leg.participants[leg.side === 'A' ? 1 : 0] ?? '') : ''}
              </span>
              <span className="odds">{fork.o?.[index] ? oddsText(fork.o[index]) : '—'}</span>
            </div>
          );
        })}

        <div className="foot">
          <span
            className="vol"
            title="медианная живучесть такой вилки у этой пары площадок; прочерк — выборки ещё нет"
          >
            {typeof fork.sv === 'number' ? `живёт ~${fork.sv}с` : 'живучесть —'}
          </span>
          <span className={`age${age > 120 ? ' age old' : ''}`}>{age}с</span>
          {/* Пометка называется словом, а не считается штуками. «⚠ 2» не говорит
              ничего: протухшая котировка и отсутствие прямой ссылки — это разные
              решения, а не «две проблемы». Полные тексты сервера — в подсказке и
              в раскрытой карточке. */}
          <span className={`badge ${flagged ? 'flagged' : 'clean'}`} title={rulesHint(rules)}>
            {badgeText(rules, flagged)}
          </span>
        </div>

        <div className="actions">
          <button type="button" className="icon" onClick={() => hide(fork.id)} title="скрыть">
            ×
          </button>
        </div>
      </div>
      {open && <ForkCard fork={fork} bank={bank} />}
    </>
  );
}
